// AccountsReceivableServiceContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import AccountCodeContainer from 'src/models/AP/AccountCodeContainer';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import ErrorResponse from 'src/models/ErrorResponse';
import { ODataFilter, ODataFilterOperators, ODataOptions } from 'src/util/ODataBuilder';
import { AnyObject } from 'final-form';
import useFetch from 'use-http';

// Mirrors ar_pastdue.fmt's Control block search fields. invoiceType matches the Oracle poplist's
// literal values ('1' = Expense Invoices, '2' = Lease Invoices, '3' = both - the "All" option isn't
// a real Oracle list entry, but the Query trigger's own `if invoice_type = 3` branch already
// supports it). pastDue mirrors the Past Due poplist ('45+'/'60+'/'90+'/'120+'); undefined means no
// age filter, matching the Oracle trigger's own no-op when none of those match.
export interface IArPastDueSearchCriteria {
  clientNumber?: number;
  projectNumber?: number;
  invoiceType: '1' | '2' | '3';
  pastDue?: '45+' | '60+' | '90+' | '120+';
}

// Mirrors arhistory.fmt's Control block search fields. invoiceType matches ar_pastdue's own
// poplist values ('1' = Expense Invoices, '2' = Lease Invoices, '3' = both). invoiceStatus mirrors
// the Invoice Status poplist ('1' = closed/paid, '2' = open/unpaid); undefined = both, same as an
// unselected value in Oracle's own WHEN-BUTTON-PRESSED (oid 49) skipping the close filter entirely.
export interface IArHistorySearchCriteria {
  clientNumber?: number;
  projectNumber?: number;
  begDate?: string;
  endDate?: string;
  invoiceType: '1' | '2' | '3';
  invoiceStatus?: '1' | '2';
}

// Mirrors icco_brkdwn.fmt's Control block search fields - Type isn't wired into QUERY_DATA at all
// (confirmed via the .fmt trace - it only feeds the Excel export dispatcher/reports), so
// runQuery ignores it, but it's carried here too since the Excel Report flow needs it.
export interface IIccoBreakdownSearchCriteria {
  begDate: string;
  endDate: string;
  type: string;
}

export const TE_URL = process.env.REACT_APP_BASE_TE_API_URL;
const CREATE_DAY_ENTRY = 'api/v2/expense/receipt/';
const CREATE_CLIENT_DEPOSIT = 'client/deposit';
const CREATE_CLIENT_DEPOSIT_CHECK = 'deposit/check';
const DELETE_DAY_ENTRY = 'api/v2/entry/delete/';

type ServiceState = {
  teUserData: AnyObject | undefined;
  accountCodes: Array<AccountCodeContainer> | undefined;
  teUserVacTime: AnyObject | undefined;
  refresh: boolean | undefined;
  updateReceipts?: AnyObject;
  timeAndExpenseToken: string | undefined;
};

// --- Define the interface for the service functions ---
export interface IAccountsReceivableServiceContext {
  serviceState: ServiceState;
  deleteDayEntry: (id: number) => Promise<void | AnyObject | undefined>;
  getClientDeposits: (depositDate: string) => Promise<void | AnyObject | undefined>;
  getClientDepositChecks: (clientNumber: number, depositNumber: number) => Promise<void | AnyObject | undefined>;
  getClientDepositInvoices: (clientNumber: number, depositNumber: number) => Promise<void | AnyObject | undefined>;
  searchClientDepositInvoices: (clientNumber: number, depositNumber: number, searchOptions: ODataOptions) => Promise<void | AnyObject | undefined>;
  getClientReconciledInvoices: (clientNumber: number, depositNumber: number) => Promise<void | AnyObject | undefined>;
  searchReconciledInvoices: (clientNumber: number, depositNumber: number, searchOptions: ODataOptions) => Promise<void | AnyObject | undefined>;
  getClients: (clientSearchOptions: ODataOptions) => Promise<void | AnyObject | undefined>;
  getArPastDueInvoices: (criteria: IArPastDueSearchCriteria) => Promise<void | AnyObject | undefined>;
  getArHistoryInvoices: (criteria: IArHistorySearchCriteria) => Promise<void | AnyObject | undefined>;
  getArHistoryPayments: (entryNumber: number) => Promise<void | AnyObject | undefined>;
  getIccoBreakdown: (criteria: IIccoBreakdownSearchCriteria) => Promise<void | AnyObject | undefined>;
  insertClientDeposit: (body: AnyObject) => Promise<void | number | undefined>;
  addClientToDeposit: (body: AnyObject) => Promise<void | number | undefined>;
  getDepositAccounts: () => Promise<void | AnyObject | undefined>;
  updateClientDepositAccounts: (clientNumber: number, depositNumber: number, body: AnyObject) => Promise<void | number | undefined>;
  getDepositDocs: (clientNumber: number, depositNumber: number) => Promise<void | AnyObject | undefined>;
  addDepositDoc: (clientNumber: number, depositNumber: number, description: string, fileName: string, file: Blob) => Promise<void | number | undefined>;
  deleteDepositDoc: (docId: number) => Promise<void | number | undefined>;
  downloadDepositDocBlob: (docId: number, fileName: string) => Promise<void>;
  downloadDepositDocFromS3: (s3Key: string, fileName: string) => Promise<void>;
  insertClientDepositCheck: (body: AnyObject, clientNumber: number) => Promise<void | number | undefined>;
  deleteClientDeposit: (clientNumber: number, depositNumber: number) => Promise<void | number | undefined>;
  moveInvoiceToReconciled: (clientNumber: number, depositNumber: number, entryNumber: number) => Promise<void | number | undefined>;
  moveInvoiceToOutstanding: (clientNumber: number, entryNumber: number) => Promise<void | number | undefined>;
  moveAllInvoicesToOutstanding: (clientNumber: number, depositNumber: number, entryNumbers: Array<number>) => Promise<void | number | undefined>;
  moveAllInvoicesToReconciled: (clientNumber: number, depositNumber: number, entryNumbers: Array<number>) => Promise<void | number | undefined>;
  getDepositReconciliationTotals: (clientNumber: number, depositNumber: number) => Promise<void | AnyObject | undefined>;
  getArReconTransLines: (clientNumber: number, depositNumber: number) => Promise<void | AnyObject | undefined>;
  postReconTransaction: (clientNumber: number, depositNumber: number, checkNumber: number, body: AnyObject) => Promise<void | number | undefined>;
  deleteClientDepositCheck: (clientNumber: number, checkNumber: number, depositNumber: number) => Promise<void | number | undefined>;
  updateClientDepositCheck: (clientNumber: number, checkNumber: number, depositNumber: number, body: AnyObject) => Promise<void | number | undefined>;
  getDepositFinalizeEligibility: (depositNumber: number) => Promise<void | AnyObject | undefined>;
  finalizeDeposit: (depositNumber: number, finalizedBy: string) => Promise<void | number | undefined>;
  postDeposit: (clientNumber: number, depositNumber: number) => Promise<void | AnyObject | undefined>;
  createDayEntry: (body: AnyObject) => Promise<void | object | undefined>;
  getClientNotes: (clientNumber: number) => Promise<void | AnyObject | undefined>;
  createClientNote: (clientNumber: number, body: AnyObject) => Promise<void | number | undefined>;
  updateClientNote: (noteId: number, text: string) => Promise<void | number | undefined>;
  setClientNoteActive: (noteId: number, active: boolean) => Promise<void | number | undefined>;
  getArAgingReport: (reportParameters: AnyObject) => Promise<void>;
  getArLedgerV3LegalReport: (reportParameters: AnyObject) => Promise<void>;
  getDepositReport: (reportParameters: AnyObject) => Promise<void>;
}

const handleApiError = (value: Response) => {
    console.log('api call failed: ' + value);
};

const handleApiCall = (value: Response) => {
    return new Promise<object>((resolve) => {
        if (value.ok) {
            value
                .json()
                .then((body) => resolve(body))
                .catch(() => resolve(null));
        } else {
            resolve(value.json().then((errorResponse: ErrorResponse) => {
              if (errorResponse?.data && !errorResponse?.message && !errorResponse?.messageCode) {
                return new ErrorResponse('500', errorResponse.data );
              } else {
                return new ErrorResponse(errorResponse.messageCode, errorResponse.message );
              }
            }));
        }
    });
};

const createHeader = (type: string, teToken: string) => {
    let headers = {};
    if (type === 'getTeData') {
      headers = { 'Tsjwt': teToken, 'content-type': 'application/json' };
    }

    return headers;
};

const postHeader = (body: object, jwt: string | undefined = undefined) => {
  const header: RequestInit = {
      body: JSON.stringify(body),
      headers: createHeader('getTeData', jwt),
      method: 'POST',
      mode: 'cors',
  };
  return header;
};

const deleteHeader = (jwt: string | undefined = undefined) => {
  const header: RequestInit = {
      headers: createHeader('getTeData', jwt),
      method: 'DELETE',
      mode: 'cors',
  };
  return header;
};

const deleteRequest = async (url: string, jwt: string | undefined = undefined) => {
  return fetch(url, deleteHeader(jwt)).then(handleApiCall).catch(handleApiError);
};

const postRequest = async (url: string, body: object, jwt: string | undefined = undefined) => {
    return fetch(url, postHeader(body, jwt)).then(handleApiCall).catch(handleApiError);
};

// --- Create the context ---
const ServiceContext = createContext<IAccountsReceivableServiceContext | undefined>(undefined);

// --- Provider component ---
export const ArServiceContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { get, post, put, del, response } = useFetch();
  const appContext = React.useContext<IAppContextState>(AppContext);
  const [serviceState, setServiceState] = useState<ServiceState>({
    timeAndExpenseToken: undefined,
    teUserData: undefined,
    accountCodes: undefined,
    teUserVacTime: undefined,
    refresh: undefined,
    updateReceipts: undefined
  });

  useEffect(() => {
    if (!serviceState.timeAndExpenseToken) {
      getTimeAndExpenseToken();
    }
  }, []);

  const getTimeAndExpenseToken = async () => {
    let userName = appContext.currentUser.username;

    const result = await get(`v1.0/reports/time-expense-token/user/${userName}`);
    if (response.ok) {
        try {
                const jwtObject = JSON.parse(result);
                setServiceState((prev) => ({
                  ...prev,
                  timeAndExpenseToken: jwtObject.timeAndExpenseJwt
                }));
            } catch (error) {
                console.log('Invalid JSON string:', error);
            }
    } else {
        console.log('Unable to get T&E token!');
    }
  };

  const getClientDeposits = async (depositDate: string) => {
    const url = '/v1.0/AccountsReceivable/client/deposits';
    const options = new ODataOptions();
    options.addFilter(new ODataFilter('depositDate', ODataFilterOperators.Equals, depositDate));
    try {
      const result = await get(url + options.toString());
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to get client deposits.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getClientDepositChecks = async (clientNumber: number, depositNumber: number) => {
    const url = `/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/checks`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to get client deposit checks.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getClientDepositInvoices = async (clientNumber: number, depositNumber: number) => {
    const url = `/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/invoices`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to get client deposit invoices.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const searchClientDepositInvoices = async (clientNumber: number, depositNumber: number, searchOptions: ODataOptions) => {
    const queryString = searchOptions?.toString();
    const url = `/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/invoices/search${queryString}`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to search client deposit invoices.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getClientReconciledInvoices = async (clientNumber: number, depositNumber: number) => {
    const url = `/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/invoices/reconciled`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
      } else {
        console.log('Unable to get client deposit invoices.');
      }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const searchReconciledInvoices = async (clientNumber: number, depositNumber: number, searchOptions: ODataOptions) => {
    const queryString = searchOptions?.toString();
    const url = `/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/invoices/reconciled/search${queryString}`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
      } else {
        console.log('Unable to search reconciled invoices.');
      }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getClients = async (clientSearchOptions: ODataOptions) => {
      try {
        const queryString = clientSearchOptions?.toString();
        let result = await get(`/v1.0/AccountsPayable/clients${queryString}`);

        if (response.ok) {
          console.log('Unable to get client deposit invoices.');
        }

        return Promise.resolve(result);
      } catch (e) {
        return Promise.reject(e);
      }
    };

  const getArPastDueInvoices = async (criteria: IArPastDueSearchCriteria) => {
    // Capped the same way CheckRegisterPage.tsx caps its own list fetch (new
    // ODataOptions().addSkipTop(0, 50)) - an unbounded AR_DETAIL fetch stalled the grid
    // rendering the result. This endpoint isn't OData-shaped (plain query params, not
    // $skip/$top), so skip/top are sent directly rather than via ODataOptions.toString().
    const params = new URLSearchParams({ invoiceType: criteria.invoiceType, skip: '0', top: '50' });
    if (criteria.clientNumber !== undefined) {
      params.set('clientNumber', String(criteria.clientNumber));
    }
    if (criteria.projectNumber !== undefined) {
      params.set('projectNumber', String(criteria.projectNumber));
    }
    if (criteria.pastDue !== undefined) {
      params.set('pastDue', criteria.pastDue);
    }

    const url = `/v1.0/AccountsReceivable/pastdue?${params.toString()}`;
    try {
      const result = await get(url);
      if (!response.ok) {
        console.log('Unable to get A/R past due invoices.');
      }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getArHistoryInvoices = async (criteria: IArHistorySearchCriteria) => {
    // Same 0/50 cap CheckRegisterPage.tsx applies to its own list fetch - see getArPastDueInvoices.
    const params = new URLSearchParams({ invoiceType: criteria.invoiceType, skip: '0', top: '50' });
    if (criteria.clientNumber !== undefined) {
      params.set('clientNumber', String(criteria.clientNumber));
    }
    if (criteria.projectNumber !== undefined) {
      params.set('projectNumber', String(criteria.projectNumber));
    }
    if (criteria.begDate !== undefined) {
      params.set('begDate', criteria.begDate);
    }
    if (criteria.endDate !== undefined) {
      params.set('endDate', criteria.endDate);
    }
    if (criteria.invoiceStatus !== undefined) {
      params.set('invoiceStatus', criteria.invoiceStatus);
    }

    const url = `/v1.0/AccountsReceivable/history?${params.toString()}`;
    try {
      const result = await get(url);
      if (!response.ok) {
        console.log('Unable to get A/R history invoices.');
      }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getArHistoryPayments = async (entryNumber: number) => {
    const url = `/v1.0/AccountsReceivable/history/${entryNumber}/payments?skip=0&top=50`;
    try {
      const result = await get(url);
      if (!response.ok) {
        console.log('Unable to get A/R history payments.');
      }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getIccoBreakdown = async (criteria: IIccoBreakdownSearchCriteria) => {
    const params = new URLSearchParams({ begDate: criteria.begDate, endDate: criteria.endDate });
    const url = `/v1.0/AccountsReceivable/icco/breakdown?${params.toString()}`;
    try {
      const result = await get(url);
      if (!response.ok) {
        console.log('Unable to get ICCO breakdown.');
      }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const deleteDayEntry = async (tmId: number) => {
    try {
      const result = await deleteRequest(`${TE_URL}${DELETE_DAY_ENTRY}${tmId}`, serviceState.timeAndExpenseToken);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to delete day entry.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const createDayEntry = async (body: AnyObject) => {
    try {
      const result = await postRequest(`${TE_URL}${CREATE_DAY_ENTRY}`, body, serviceState.timeAndExpenseToken);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to create day entry.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const insertClientDeposit = async (body: AnyObject) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/` + CREATE_CLIENT_DEPOSIT, body);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to create client deposit.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const addClientToDeposit = async (body: AnyObject) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/deposit/add-client`, body);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to add client to deposit.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getDepositAccounts = async () => {
    try {
      const result = await get(`/v1.0/AccountsReceivable/deposit-accounts`);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to get deposit accounts.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const updateClientDepositAccounts = async (clientNumber: number, depositNumber: number, body: AnyObject) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/accounts`, body);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to update client deposit accounts.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getDepositDocs = async (clientNumber: number, depositNumber: number) => {
    try {
      const result = await get(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/documents`);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to get deposit documents.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getJwtToken = (): string => {
    return localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
  };

  const addDepositDoc = async (clientNumber: number, depositNumber: number, description: string, fileName: string, file: Blob) => {
    // Raw fetch (matching InvoiceMaintenancePageComponent's document upload) since this sends the
    // file's raw bytes as the body - use-http's post() is built around JSON payloads.
    const params = new URLSearchParams({ description: description || '', fileName });
    const baseUrl = process.env.REACT_APP_BASE_API_URL;
    const url = `${baseUrl}/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/documents?${params}`;

    try {
      const result = await fetch(url, {
        method: 'POST',
        body: file,
        headers: {
          Authorization: `Bearer ${getJwtToken()}`,
          'Content-Type': file.type || 'application/octet-stream'
        }
      });

      if (!result.ok) {
        throw new Error(`HTTP ${result.status}`);
      }

      return await result.json();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const deleteDepositDoc = async (docId: number) => {
    try {
      const result = await del(`/v1.0/AccountsReceivable/deposit/documents/${docId}`);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to delete deposit document.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadDepositDocBlob = async (docId: number, fileName: string) => {
    await get(`/v1.0/AccountsReceivable/deposit/documents/${docId}/blob`);
    if (response.ok) {
      const blob = await response.blob();
      downloadBlobResponseAsFile(blob, fileName);
    } else {
      console.log('Unable to download deposit document.');
    }
  };

  const downloadDepositDocFromS3 = async (s3Key: string, fileName: string) => {
    const safeKey = encodeURIComponent(s3Key);
    await get(`/v1.0/s3bucket/${safeKey}`);
    if (response.ok) {
      const blob = await response.blob();
      downloadBlobResponseAsFile(blob, fileName);
    } else {
      console.log('Unable to download deposit document from S3.');
    }
  };

  const insertClientDepositCheck = async (body: AnyObject, clientNumber: number) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/` + CREATE_CLIENT_DEPOSIT_CHECK, body);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to create client deposit check.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const deleteClientDeposit = async (clientNumber: number, depositNumber: number) => {
    try {
      const result = await del(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}`);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to delete client deposit.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const deleteClientDepositCheck = async (clientNumber: number, checkNumber: number, depositNumber: number) => {
    try {
      const result = await del(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/check/${checkNumber}`);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to delete client deposit check.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const updateClientDepositCheck = async (clientNumber: number, checkNumber: number, depositNumber: number, body: AnyObject) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/check/${checkNumber}`, body);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to update client deposit check.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getDepositFinalizeEligibility = async (depositNumber: number) => {
    const url = `/v1.0/AccountsReceivable/deposit/${depositNumber}/finalize-eligibility`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to get deposit finalize eligibility.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const finalizeDeposit = async (depositNumber: number, finalizedBy: string) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/deposit/${depositNumber}/finalize?finalizedBy=${encodeURIComponent(finalizedBy)}`, {});
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to finalize deposit.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const postDeposit = async (clientNumber: number, depositNumber: number) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/post`, {});
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to post deposit.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const moveInvoiceToReconciled = async (clientNumber: number, depositNumber: number, entryNumber: number) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/invoice/${entryNumber}/reconcile`, {});
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to move invoice to reconciled.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const moveInvoiceToOutstanding = async (clientNumber: number, entryNumber: number) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/invoice/${entryNumber}/unreconcile`, {});
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to move invoice to outstanding.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const moveAllInvoicesToOutstanding = async (clientNumber: number, depositNumber: number, entryNumbers: Array<number>) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/invoices/unreconcile-all`, entryNumbers);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to move all invoices to outstanding.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const moveAllInvoicesToReconciled = async (clientNumber: number, depositNumber: number, entryNumbers: Array<number>) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/invoices/reconcile-all`, entryNumbers);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to move all invoices to reconciled.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getDepositReconciliationTotals = async (clientNumber: number, depositNumber: number) => {
    const url = `/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/totals`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to get deposit reconciliation totals.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getArReconTransLines = async (clientNumber: number, depositNumber: number) => {
    const url = `/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/ar-recon-trans-lines`;
    try {
      const result = await get(url);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from T&E Service', result);
    } else {
      console.log('Unable to get AR recon trans lines.');
    }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const postReconTransaction = async (clientNumber: number, depositNumber: number, checkNumber: number, body: AnyObject) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/deposit/${depositNumber}/check/${checkNumber}/post-recon-transaction`, body);
      if (response.ok && (!(result as {error: string})?.error || (result as {error: string})?.error?.length === 0)) {
        console.log('========> Received response from Kudu API Service', result);
    } else {
      console.log('Unable to post recon transaction.');
    }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const getClientNotes = async (clientNumber: number) => {
    try {
      const result = await get(`/v1.0/AccountsReceivable/client/${clientNumber}/notes`);
      if (!response.ok) {
        console.log('Unable to get client notes.');
      }
      return Promise.resolve(result);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const createClientNote = async (clientNumber: number, body: AnyObject) => {
    try {
      const result = await post(`/v1.0/AccountsReceivable/client/${clientNumber}/notes`, body);
      if (!response.ok) {
        console.log('Unable to create client note.');
      }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const updateClientNote = async (noteId: number, text: string) => {
    try {
      const result = await put(`/v1.0/AccountsReceivable/notes/${noteId}`, { text });
      if (!response.ok) {
        console.log('Unable to update client note.');
      }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const setClientNoteActive = async (noteId: number, active: boolean) => {
    try {
      const result = await put(`/v1.0/AccountsReceivable/notes/${noteId}/active?active=${active}`, {});
      if (!response.ok) {
        console.log('Unable to update client note active state.');
      }
      return result;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  // POST (not GET, per usp_ARAgingReport's parameters being far too long/dynamic for a query
  // string) that still expects a PDF blob back rather than JSON - mirrors downloadDepositDocBlob's
  // response.blob()/downloadBlobResponseAsFile pattern rather than the postRequest()/
  // handleApiCall() JSON-parsing path used everywhere else in this file.
  const getArAgingReport = async (reportParameters: AnyObject) => {
    try {
      await post('/v1.0/AccountsReceivable/report/aging', reportParameters);
      if (response.ok) {
        const blob = await response.blob();
        downloadBlobResponseAsFile(blob, 'ARAging.pdf');
      } else {
        console.log('Unable to generate A/R Aging report.');
      }
    } catch (e) {
      throw e;
    }
  };

  const getArLedgerV3LegalReport = async (reportParameters: AnyObject) => {
    try {
      await post('/v1.0/AccountsReceivable/report/detailed-aging-legal', reportParameters);
      if (response.ok) {
        const blob = await response.blob();
        downloadBlobResponseAsFile(blob, 'ARLedgerV3Legal.pdf');
      } else {
        console.log('Unable to generate A/R Detailed Aging (Post Date/Legal) report.');
      }
    } catch (e) {
      throw e;
    }
  };

  const getDepositReport = async (reportParameters: AnyObject) => {
    try {
      await post('/v1.0/AccountsReceivable/report/deposits', reportParameters);
      if (response.ok) {
        const blob = await response.blob();
        downloadBlobResponseAsFile(blob, `${reportParameters?.reportName || 'Deposits'}.pdf`);
      } else {
        console.log('Unable to generate Deposit report.');
      }
    } catch (e) {
      throw e;
    }
  };

  // --- Provide all functions in context value ---
  const value: IAccountsReceivableServiceContext = {
    serviceState,
    createDayEntry,
    deleteDayEntry,
    getClientDeposits,
    getClientDepositChecks,
    getClientDepositInvoices,
    searchClientDepositInvoices,
    getClientReconciledInvoices,
    searchReconciledInvoices,
    getClients,
    getArPastDueInvoices,
    getArHistoryInvoices,
    getArHistoryPayments,
    getIccoBreakdown,
    insertClientDeposit,
    addClientToDeposit,
    getDepositAccounts,
    updateClientDepositAccounts,
    getDepositDocs,
    addDepositDoc,
    deleteDepositDoc,
    downloadDepositDocBlob,
    downloadDepositDocFromS3,
    insertClientDepositCheck,
    deleteClientDeposit,
    moveInvoiceToReconciled,
    moveInvoiceToOutstanding,
    moveAllInvoicesToOutstanding,
    moveAllInvoicesToReconciled,
    getDepositReconciliationTotals,
    getArReconTransLines,
    postReconTransaction,
    deleteClientDepositCheck,
    updateClientDepositCheck,
    getDepositFinalizeEligibility,
    finalizeDeposit,
    postDeposit,
    getClientNotes,
    createClientNote,
    updateClientNote,
    setClientNoteActive,
    getArAgingReport,
    getArLedgerV3LegalReport,
    getDepositReport
  };

  return serviceState.timeAndExpenseToken === null ? <div>Loading...</div> : (
    <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>
  );
};

// --- Hook for components to consume the service context ---
export const useArServiceCtx = (): IAccountsReceivableServiceContext => {
  const ctx = useContext(ServiceContext);
  if (!ctx) {
    throw new Error('useArServiceCtx must be used within ServiceContextProvider');
  }
  return ctx;
};


// WEBPACK FOOTER //
// ./src/components/accounts-receivable/AccountsReceivableServiceContext.tsx